import React, { useState, useEffect } from "react";
import { useFetchMedia } from "../hooks/useFetchMedia";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const MediaOutletsCarousel = ({ limit = 50 }) => {
  const {
    data: response,
    loading,
    error,
  } = useFetchMedia(apiEndpoints.mediaOutlets.latest(limit));

  const [currentGroup, setCurrentGroup] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(10);

  // Extraer los datos de medios de la respuesta
  const mediaOutlets = response?.data || [];

  // Función para determinar items por vista según el ancho de pantalla
  const updateItemsPerView = () => {
    const width = window.innerWidth;
    if (width <= 576) {
      setItemsPerView(2); // Móvil: 2 medios por vista
    } else if (width <= 768) {
      setItemsPerView(3); // Tablet pequeña: 3 medios
    } else if (width <= 992) {
      setItemsPerView(5); // Tablet: 5 medios
    } else if (width <= 1200) {
      setItemsPerView(8); // Desktop pequeño: 8 medios
    } else {
      setItemsPerView(10); // Desktop grande: 10 medios
    }
  };

  // Configurar items por vista en el montaje y cambios de tamaño
  useEffect(() => {
    updateItemsPerView();
    
    const handleResize = () => updateItemsPerView();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dividir los medios en grupos de itemsPerView
  const mediaGroups = [];
  for (let i = 0; i < mediaOutlets.length; i += itemsPerView) {
    mediaGroups.push(mediaOutlets.slice(i, i + itemsPerView));
  }

  const totalGroups = mediaGroups.length;

  // Auto-scroll cada 4 segundos
  useEffect(() => {
    if (totalGroups <= 1) return;

    const interval = setInterval(() => {
      nextGroup();
    }, 4000);

    return () => clearInterval(interval);
  }, [totalGroups, currentGroup, itemsPerView]);

  const nextGroup = () => {
    if (isAnimating || totalGroups <= 1) return;

    setIsAnimating(true);
    setCurrentGroup((prev) => (prev + 1) % totalGroups);

    setTimeout(() => setIsAnimating(false), 600);
  };

  const prevGroup = () => {
    if (isAnimating || totalGroups <= 1) return;

    setIsAnimating(true);
    setCurrentGroup((prev) => (prev - 1 + totalGroups) % totalGroups);

    setTimeout(() => setIsAnimating(false), 600);
  };

  const goToGroup = (groupIndex) => {
    if (isAnimating || groupIndex === currentGroup) return;

    setIsAnimating(true);
    setCurrentGroup(groupIndex);

    setTimeout(() => setIsAnimating(false), 600);
  };

  if (loading) {
    return (
      <div className="media-outlets-carousel">
        <div className="carousel-container">
          <h2 className="carousel-title">📺 Medios Asociados</h2>
          <div className="carousel-loading">
            <div className="loading-spinner">⏳ Cargando medios...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="media-outlets-carousel">
        <div className="carousel-container">
          <h2 className="carousel-title">📺 Medios Asociados</h2>
          <div className="carousel-error">
            ⚠ Error al cargar medios: {error}
          </div>
        </div>
      </div>
    );
  }

  if (response && response.success === false) {
    return (
      <div className="media-outlets-carousel">
        <div className="carousel-container">
          <h2 className="carousel-title">📺 Medios Asociados</h2>
          <div className="carousel-error">
            ⚠ Error de API: {response.message || "Error desconocido"}
          </div>
        </div>
      </div>
    );
  }

  if (!mediaOutlets.length) {
    return (
      <div className="media-outlets-carousel">
        <div className="carousel-container">
          <h2 className="carousel-title">📺 Medios Asociados</h2>
          <div className="carousel-empty">🔭 No hay medios disponibles</div>
        </div>
      </div>
    );
  }

  return (
    <div className="media-outlets-carousel">
      <style>{`
        .media-outlets-carousel {
          background: linear-gradient(135deg, #667eea 0%, #72a24bff 100%);
          padding: 20px 0;
          overflow: hidden;
        }

        .carousel-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .carousel-title {
          text-align: center;
          font-size: 2.5rem;
          font-weight: bold;
          color: white;
          margin-bottom: 40px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .carousel-wrapper {
          position: relative;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          backdrop-filter: blur(10px);
          padding: 40px 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .carousel-viewport {
          position: relative;
          width: 100%;
          height: 200px;
        }

        .carousel-track {
          display: flex;
          transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          transform: translateX(-${currentGroup * 100}%);
          height: 100%;
        }

        .carousel-group {
          min-width: 100%;
          display: grid;
          grid-template-columns: repeat(${itemsPerView}, 1fr);
          gap: 20px;
          align-items: center;
          padding: 0 10px;
        }

        .media-outlet-card {
          background: white;
          border-radius: 15px;
          padding: 20px 15px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          height: 160px;
          justify-content: center;
          cursor: pointer;
        }

        .media-outlet-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        .outlet-logo {
          width: 80px;
          height: 80px;
          object-fit: contain;
          border-radius: 10px;
          margin-bottom: 12px;
          background: #f8f9fa;
          padding: 10px;
        }

        .outlet-logo-placeholder {
          width: 80px;
          height: 80px;
          background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin-bottom: 12px;
        }

        .outlet-name {
          font-size: 1rem;
          font-weight: bold;
          color: #333;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          max-height: 2.4em;
        }

        .outlet-name a {
          color: #333;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .outlet-name a:hover {
          color: #667eea;
        }

        .carousel-navigation {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          margin-top: 30px;
        }

        .nav-button {
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .nav-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .carousel-dots {
          display: flex;
          gap: 8px;
        }

        .carousel-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .carousel-dot.active {
          background: white;
          transform: scale(1.3);
        }

        .carousel-info {
          text-align: center;
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
          margin-bottom: 20px;
        }

        .carousel-loading,
        .carousel-error,
        .carousel-empty {
          text-align: center;
          padding: 60px 20px;
          color: white;
          font-size: 1.2rem;
        }

        .loading-spinner {
          font-size: 1.5rem;
        }

        /* Responsive Design - Específico para móvil */
        @media (max-width: 576px) {
          .carousel-viewport {
            height: 180px;
          }

          .media-outlet-card {
            height: 140px;
            padding: 15px 10px;
          }

          .outlet-logo,
          .outlet-logo-placeholder {
            width: 70px;
            height: 70px;
          }

          .outlet-name {
            font-size: 0.9rem;
          }

          .carousel-title {
            font-size: 1.8rem;
            margin-bottom: 30px;
          }

          .carousel-wrapper {
            padding: 30px 15px;
          }

          .carousel-group {
            gap: 15px;
          }
        }

        @media (max-width: 768px) {
          .carousel-title {
            font-size: 2rem;
          }

          .outlet-logo,
          .outlet-logo-placeholder {
            width: 75px;
            height: 75px;
          }

          .outlet-name {
            font-size: 0.95rem;
          }
        }

        @media (max-width: 992px) {
          .carousel-title {
            font-size: 2.2rem;
          }
        }
      `}</style>

      <div className="carousel-container">
        <h2 className="carousel-title">Medios Asociados</h2>

        {totalGroups > 0 && (
          <div className="carousel-info">
            Mostrando grupo {currentGroup + 1} de {totalGroups} ({mediaOutlets.length} medios en total)
          </div>
        )}

        <div className="carousel-wrapper">
          <div className="carousel-viewport">
            <div className="carousel-track">
              {mediaGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="carousel-group">
                  {group.map((outlet, index) => (
                    <div
                      key={outlet.id || `${groupIndex}-${index}`}
                      className="media-outlet-card"
                      onClick={() => {
                        if (outlet.website_url) {
                          window.open(outlet.website_url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      {outlet.logo ? (
                        <img
                          src={`${API_BASE_URL}${outlet.logo}`}
                          alt={outlet.name}
                          className="outlet-logo"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}

                      <div
                        className="outlet-logo-placeholder"
                        style={{ display: outlet.logo ? "none" : "flex" }}
                      >
                        📺
                      </div>

                      <div className="outlet-name" title={outlet.name}>
                        {outlet.website_url ? (
                          <a
                            href={outlet.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {outlet.name}
                          </a>
                        ) : (
                          <span>{outlet.name}</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Llenar espacios vacíos si el grupo tiene menos elementos */}
                  {group.length < itemsPerView &&
                    Array.from({ length: itemsPerView - group.length }).map(
                      (_, emptyIndex) => (
                        <div
                          key={`empty-${groupIndex}-${emptyIndex}`}
                          className="media-outlet-card"
                          style={{ opacity: 0, cursor: 'default' }}
                        ></div>
                      )
                    )}
                </div>
              ))}
            </div>
          </div>

          {totalGroups > 1 && (
            <div className="carousel-navigation">
              <button
                className="nav-button"
                onClick={prevGroup}
                disabled={isAnimating}
                title="Grupo anterior"
              >
                ‹
              </button>

              <div className="carousel-dots">
                {mediaGroups.map((_, index) => (
                  <div
                    key={index}
                    className={`carousel-dot ${
                      index === currentGroup ? "active" : ""
                    }`}
                    onClick={() => goToGroup(index)}
                    title={`Grupo ${index + 1}`}
                  />
                ))}
              </div>

              <button
                className="nav-button"
                onClick={nextGroup}
                disabled={isAnimating}
                title="Grupo siguiente"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};